import { z } from 'zod';
import { expandActionPattern } from './pattern.js';
import { ANY_RESOURCE } from './resources.js';

export const POLICY_DOCUMENT_VERSION = '2026-10-01';

export enum PolicyEffectEnum {
  Allow = 'Allow',
  /** Overrides any Allow for the same action, from any policy. */
  Deny = 'Deny',
}
export const PolicyEffect = z.enum(PolicyEffectEnum);

/** An exact action or a `*` pattern. Must match at least one known action, so typos are rejected. */
export const ActionPatternSchema = z
  .string()
  .min(1)
  .max(128)
  .refine((pattern) => expandActionPattern(pattern).length > 0, {
    message: 'Action pattern does not match any known action',
  });

/** Only `*` is supported until resource-level scoping is implemented. */
export const ResourcePatternSchema = z.literal(ANY_RESOURCE);

export const PolicyStatementSchema = z.strictObject({
  effect: PolicyEffect,
  actions: z.array(ActionPatternSchema).min(1).max(256),
  resources: z.array(ResourcePatternSchema).min(1).max(1),
});
export type PolicyStatement = z.infer<typeof PolicyStatementSchema>;

export const PolicyDocumentSchema = z.strictObject({
  version: z.literal(POLICY_DOCUMENT_VERSION),
  statements: z.array(PolicyStatementSchema).max(64),
});
export type PolicyDocument = z.infer<typeof PolicyDocumentSchema>;

/**
 * Statement shape for transport (sessions, API responses) and evaluation. Deliberately
 * lenient about action names: documents are validated strictly by
 * {@link PolicyDocumentSchema} on write, and an action unknown to this build simply
 * matches nothing, so a newer server can't break an older client's session parsing.
 */
export const EvaluableStatementSchema = z.object({
  effect: PolicyEffect,
  actions: z.array(z.string().max(128)).max(256),
  resources: z.array(z.string().max(512)).max(64),
});
export type EvaluableStatement = z.infer<typeof EvaluableStatementSchema>;

/** Lenient read of a stored document; see {@link EvaluableStatementSchema}. */
export const StoredPolicyDocumentSchema = z.object({
  version: z.literal(POLICY_DOCUMENT_VERSION),
  statements: z.array(EvaluableStatementSchema).max(64),
});
export type StoredPolicyDocument = z.infer<typeof StoredPolicyDocumentSchema>;

export enum PolicyManagedByEnum {
  Builtin = 'builtin',
  Customer = 'customer',
}
export const PolicyManagedBy = z.enum(PolicyManagedByEnum);
export type PolicyManagedBy = z.infer<typeof PolicyManagedBy>;

export function allowStatement(actions: string[]): PolicyStatement {
  return { effect: PolicyEffectEnum.Allow, actions, resources: [ANY_RESOURCE] };
}

export function denyStatement(actions: string[]): PolicyStatement {
  return { effect: PolicyEffectEnum.Deny, actions, resources: [ANY_RESOURCE] };
}
